import React, { useState, useEffect, useRef } from 'react';
import {
  Rss, ImagePlus, X, Loader2, Trash2, Send, Pencil, Check,
  Heart, MessageCircle, School, RefreshCw,
} from 'lucide-react';
import { toastMessage } from '@/utils/toast-message';
import { classFeedApi, PaClass, FeedPost } from './class-feed.api';

const MAX_CHARS = 280;
const MAX_IMAGES = 15;

function isEditable(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < 24 * 60 * 60 * 1000;
}

function formatTimeAgo(dateString: string): string {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
}

const ClassFeed: React.FC = () => {
  const [classes, setClasses] = useState<PaClass[]>([]);
  const [curriculumLabelMap, setCurriculumLabelMap] = useState<Record<number, string>>({});
  const [selectedClassId, setSelectedClassId] = useState<number | null | undefined>(null);
  // Dropdown filter state
  const [filterCurriculumClassId, setFilterCurriculumClassId] = useState<number | ''>('');
  const [filterSections, setFilterSections] = useState<PaClass[]>([]);
  const [caption, setCaption] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [posting, setPosting] = useState(false);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [editImages, setEditImages] = useState<File[]>([]);
  const [editPreviews, setEditPreviews] = useState<string[]>([]);
  const [editExistingMedia, setEditExistingMedia] = useState<{ id: number; media_url: string }[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    classFeedApi.getClasses().then(res => {
      if (res.success && res.data) {
        const raw = (res.data as any).data ?? res.data;
        const active: PaClass[] = (Array.isArray(raw) ? raw : []).filter((c: PaClass) => c.is_active);
        setClasses(active);
      }
    });
    // Fetch grouped curriculum classes to get proper base class labels for section headers
    import('@/utils/api-request').then(({ apiRequest }) => {
      apiRequest<any>('/api/parent-app/admin/classes').then(res => {
        const grouped: any[] = res?.data?.grouped ?? [];
        const map: Record<number, string> = {};
        grouped.forEach(g => (g.classes ?? []).forEach((c: any) => { map[c.id] = c.class_label; }));
        setCurriculumLabelMap(map);
      });
    });
  }, []);

  const loadPosts = (classId: number | null | undefined, pg = 1) => {
    setLoadingPosts(true);
    setPosts([]);
    setPage(1);
    setHasMore(false);
    classFeedApi.getPosts(classId ?? undefined, pg).then(res => {
      if (res.success && res.data) {
        const d = res.data as any;
        const list = d.data ?? [];
        setPosts(list);
        setHasMore(list.length > 0 && d.page < d.pages);
      }
    }).finally(() => setLoadingPosts(false));
  };

  useEffect(() => {
    if (selectedClassId === undefined) return;
    loadPosts(selectedClassId ?? null);
  }, [selectedClassId]);

  const loadMore = () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    classFeedApi.getPosts(selectedClassId ?? undefined, nextPage).then(res => {
      if (res.success && res.data) {
        const d = res.data as any;
        const list = d.data ?? [];
        setPosts(prev => [...prev, ...list]);
        setPage(nextPage);
        setHasMore(list.length > 0 && d.page < d.pages);
      }
    }).finally(() => setLoadingMore(false));
  };

  const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const remaining = MAX_IMAGES - images.length;
    const picked = files.slice(0, remaining);
    setImages(prev => [...prev, ...picked]);
    setPreviews(prev => [...prev, ...picked.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removeImage = (i: number) => {
    URL.revokeObjectURL(previews[i]);
    setImages(prev => prev.filter((_, idx) => idx !== i));
    setPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const handlePost = async () => {
    if (!caption.trim()) return toastMessage('Write something first', 'warning');
    setPosting(true);
    const res = await classFeedApi.createPost({ class_id: selectedClassId ?? null, caption }, images);
    setPosting(false);
    if (res.success) {
      toastMessage(selectedClassId ? 'Posted to class feed' : 'Posted to all classes', 'success');
      setCaption('');
      previews.forEach(p => URL.revokeObjectURL(p));
      setImages([]);
      setPreviews([]);
      loadPosts(selectedClassId);
    } else {
      toastMessage(res.message ?? 'Failed to post', 'error');
    }
  };

  const handleDelete = async (postId: number) => {
    if (!window.confirm('Delete this post? Parents will no longer see it.')) return;
    setDeletingId(postId);
    const res = await classFeedApi.deletePost(postId);
    setDeletingId(null);
    if (res.success) {
      setPosts(prev => prev.filter(p => p.id !== postId));
      toastMessage('Post deleted', 'success');
    } else {
      toastMessage('Failed to delete', 'error');
    }
  };

  const startEdit = (post: FeedPost) => {
    setEditingId(post.id);
    setEditCaption(post.caption);
    setEditExistingMedia(post.media.map(m => ({ id: m.id, media_url: m.media_url })));
    setEditImages([]);
    setEditPreviews([]);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditCaption('');
    setEditImages([]);
    editPreviews.forEach(p => URL.revokeObjectURL(p));
    setEditPreviews([]);
    setEditExistingMedia([]);
  };

  const removeExistingMedia = async (postId: number, mediaId: number) => {
    const res = await classFeedApi.deletePostMedia(postId, mediaId);
    if (res.success) {
      setEditExistingMedia(prev => prev.filter(m => m.id !== mediaId));
    } else {
      toastMessage('Failed to remove image', 'error');
    }
  };

  const handleEditImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const remaining = MAX_IMAGES - editExistingMedia.length - editImages.length;
    const picked = files.slice(0, remaining);
    setEditImages(prev => [...prev, ...picked]);
    setEditPreviews(prev => [...prev, ...picked.map(f => URL.createObjectURL(f))]);
    e.target.value = '';
  };

  const removeNewEditImage = (i: number) => {
    URL.revokeObjectURL(editPreviews[i]);
    setEditImages(prev => prev.filter((_, idx) => idx !== i));
    setEditPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const saveEdit = async (postId: number) => {
    if (!editCaption.trim()) return;
    setSavingEdit(true);
    const res = await classFeedApi.updatePost(postId, editCaption.trim(), editImages);
    setSavingEdit(false);
    if (res.success) {
      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p,
        caption: editCaption.trim(),
        media: (res.data as any)?.post?.media ?? p.media,
      } : p));
      cancelEdit();
      toastMessage('Post updated', 'success');
    } else {
      toastMessage('Failed to update post', 'error');
    }
  };

  const selectedClass = classes.find(c => c.id === selectedClassId);
  const charsLeft = MAX_CHARS - caption.length;

  // Group classes by curriculum_class_id for dropdowns
  const classGroups = React.useMemo(() => {
    const groups: Map<number, PaClass[]> = new Map();
    for (const c of classes) {
      const key = c.curriculum_class_id ?? c.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }
    return [...groups.values()];
  }, [classes]);

  const handleClassFilter = (val: string) => {
    const numVal = Number(val) || '';
    setFilterCurriculumClassId(numVal as number | '');
    if (!numVal) {
      // All Classes
      setFilterSections([]);
      setSelectedClassId(null);
      return;
    }
    const group = classGroups.find(g => (g[0].curriculum_class_id ?? g[0].id) === Number(numVal));
    if (!group) return;
    if (group.length === 1) {
      setFilterSections([]);
      setSelectedClassId(group[0].id);
    } else {
      setFilterSections(group);
      setSelectedClassId(undefined); // wait for section
    }
  };

  const handleSectionFilter = (val: string) => {
    const id = Number(val) || null;
    if (id) setSelectedClassId(id);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="page-head">
        <div className="page-head-icon"><Rss size={24} /></div>
        <div className="page-head-text">
          <h1>Class Feed</h1>
          <p>Post announcements and photos to parent feeds</p>
        </div>
        <div className="page-head-actions">
          <select className="select" style={{ width: 'auto' }} value={filterCurriculumClassId} onChange={e => handleClassFilter(e.target.value)}>
            <option value="">All Classes</option>
            {classGroups.map(group => {
              const base = group[0];
              const key = base.curriculum_class_id ?? base.id;
              const label = base.curriculum_class_id != null
                ? (curriculumLabelMap[base.curriculum_class_id] ?? base.class_name)
                : base.class_name;
              return <option key={key} value={key}>{label}</option>;
            })}
          </select>
          {filterSections.length > 0 && (
            <select className="select" style={{ width: 'auto' }} defaultValue="" onChange={e => handleSectionFilter(e.target.value)}>
              <option value="">All Sections</option>
              {filterSections.map(c => {
                const baseLabel = c.curriculum_class_id != null ? (curriculumLabelMap[c.curriculum_class_id] ?? '') : '';
                const sectionPart = baseLabel && c.class_name.startsWith(baseLabel)
                  ? c.class_name.slice(baseLabel.length).trim()
                  : c.class_name;
                return <option key={c.id} value={c.id}>{sectionPart}</option>;
              })}
            </select>
          )}
          <button onClick={() => loadPosts(selectedClassId ?? null)} className="icon-btn" title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>

            {/* Compose card */}
            <div className="card" style={{ marginBottom: 24, overflow: 'hidden' }}>
              <div style={{ padding: 16 }}>
                <div className="row gap-12" style={{ marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: 'var(--slate)', color: 'var(--accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <School size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>School Admin</p>
                    <p className="meta" style={{ marginTop: 2 }}>
                      {selectedClass ? `Posting to ${selectedClass.class_name}` : 'Posting to all classes'}
                    </p>
                  </div>
                </div>

                <textarea
                  value={caption}
                  onChange={e => setCaption(e.target.value.slice(0, MAX_CHARS))}
                  placeholder="Write an update, announcement, or share a moment from class…"
                  rows={3}
                  className="textarea"
                  style={{ marginBottom: previews.length > 0 ? 12 : 0, resize: 'none' }}
                />

                {previews.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {previews.map((src, i) => (
                      <div key={i} style={{ position: 'relative', width: 76, height: 76, borderRadius: 'var(--r-input)', overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0 }}
                        onMouseEnter={e => (e.currentTarget.querySelector('button') as HTMLElement)?.style.setProperty('opacity', '1')}
                        onMouseLeave={e => (e.currentTarget.querySelector('button') as HTMLElement)?.style.setProperty('opacity', '0')}
                      >
                        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button onClick={() => removeImage(i)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', transition: 'opacity 0.15s' }}>
                          <X size={15} color="white" />
                        </button>
                      </div>
                    ))}
                    {images.length < MAX_IMAGES && (
                      <button onClick={() => fileInputRef.current?.click()} style={{ width: 76, height: 76, borderRadius: 'var(--r-input)', border: '2px dashed var(--border-2)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', cursor: 'pointer', transition: 'all 0.14s', flexShrink: 0 }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-deep)'; (e.currentTarget as HTMLElement).style.color = 'var(--accent-deep)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-2)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-3)'; }}
                      >
                        <ImagePlus size={18} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="row" style={{ padding: '10px 16px', borderTop: '1px solid var(--divider)', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="row gap-8">
                  <button onClick={() => fileInputRef.current?.click()} disabled={images.length >= MAX_IMAGES} className="icon-btn" title="Add photo" style={{ opacity: images.length >= MAX_IMAGES ? 0.5 : 1 }}>
                    <ImagePlus size={17} />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImagePick} style={{ display: 'none' }} />
                  {images.length > 0 && <span className="meta" style={{ marginTop: 0, color: 'var(--accent-deep)' }}>{images.length}/{MAX_IMAGES}</span>}
                </div>
                <div className="row gap-10">
                  <span className="meta" style={{ marginTop: 0, color: charsLeft < 30 ? (charsLeft < 0 ? 'var(--danger)' : 'var(--warning)') : 'var(--text-3)' }}>{charsLeft}</span>
                  <button onClick={handlePost} disabled={posting || !caption.trim()} className="btn btn-primary btn-sm">
                    {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Post
                  </button>
                </div>
              </div>
            </div>

            {/* Posts list */}
            {loadingPosts ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-3)' }} />
              </div>
            ) : posts.length === 0 ? (
              <div className="empty">
                <div className="empty-art"><Rss size={44} /></div>
                <h3>No posts yet</h3>
                <p>{selectedClass ? `No posts in ${selectedClass.class_name} yet.` : 'No posts across any class yet.'}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {posts.map(post => {
                  const isAdmin = post.author_type === 'admin';
                  return (
                    <div key={post.id} className="card" style={{ overflow: 'hidden' }}>

                      {/* Post header */}
                      <div className="row gap-12" style={{ padding: '14px 16px 10px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div className="row gap-10" style={{ flex: 1, minWidth: 0 }}>
                          {/* Avatar */}
                          {isAdmin ? (
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--slate)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <School size={17} />
                            </div>
                          ) : (
                            <div className="avatar cat-teal" style={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>T</div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="row gap-8" style={{ marginBottom: 3, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>
                                {isAdmin ? 'School Admin' : 'Teacher'}
                              </span>
                              <span className={`chip chip-sm ${isAdmin ? '' : 'cat-teal'}`} style={{ fontSize: 11 }}>
                                {isAdmin ? 'Admin' : 'Teacher'}
                              </span>
                              {post.class && (
                                <span className="chip chip-sm" style={{ fontSize: 11 }}>{post.class.class_name}</span>
                              )}
                            </div>
                            <p className="meta" style={{ marginTop: 0 }}>{formatTimeAgo(post.created_at)}</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="row gap-6">
                          {editingId !== post.id && (
                            <button
                              onClick={() => startEdit(post)}
                              disabled={deletingId === post.id || !isEditable(post.created_at)}
                              className="icon-btn"
                              style={{ opacity: deletingId === post.id || !isEditable(post.created_at) ? 0.4 : 1 }}
                              title={!isEditable(post.created_at) ? 'Posts can only be edited within 24 hours of publishing' : 'Edit post'}
                            >
                              <Pencil size={15} />
                            </button>
                          )}
                          <button onClick={() => handleDelete(post.id)} disabled={deletingId === post.id || editingId === post.id} className="icon-btn" title="Delete post" style={{ opacity: deletingId === post.id || editingId === post.id ? 0.5 : 1 }}>
                            {deletingId === post.id ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={15} style={{ color: 'var(--danger)' }} />}
                          </button>
                        </div>
                      </div>

                      {/* Caption */}
                      {editingId === post.id ? (
                        <div style={{ margin: '0 16px 12px', borderRadius: 'var(--r-input)', border: '2px solid var(--accent)', background: 'var(--accent-wash)', padding: 12 }}>
                          <p className="meta" style={{ margin: '0 0 10px', color: 'var(--accent-deep)' }}>Editing post</p>
                          <textarea value={editCaption} onChange={e => setEditCaption(e.target.value.slice(0, MAX_CHARS))} rows={3} className="textarea" autoFocus style={{ marginBottom: 10 }} />

                          {/* Image editing */}
                          {(editExistingMedia.length > 0 || editPreviews.length > 0) && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                              {editExistingMedia.map(m => (
                                <div key={m.id} style={{ position: 'relative', width: 72, height: 72, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}
                                  onMouseEnter={e => (e.currentTarget.querySelector('button') as HTMLElement)?.style.setProperty('opacity', '1')}
                                  onMouseLeave={e => (e.currentTarget.querySelector('button') as HTMLElement)?.style.setProperty('opacity', '0')}
                                >
                                  <img src={m.media_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  <button onClick={() => removeExistingMedia(post.id, m.id)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', transition: 'opacity 0.15s' }}>
                                    <X size={14} color="white" />
                                  </button>
                                </div>
                              ))}
                              {editPreviews.map((src, i) => (
                                <div key={`new-${i}`} style={{ position: 'relative', width: 72, height: 72, borderRadius: 8, overflow: 'hidden', flexShrink: 0, outline: '2px solid var(--accent)' }}
                                  onMouseEnter={e => (e.currentTarget.querySelector('button') as HTMLElement)?.style.setProperty('opacity', '1')}
                                  onMouseLeave={e => (e.currentTarget.querySelector('button') as HTMLElement)?.style.setProperty('opacity', '0')}
                                >
                                  <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  <button onClick={() => removeNewEditImage(i)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', transition: 'opacity 0.15s' }}>
                                    <X size={14} color="white" />
                                  </button>
                                </div>
                              ))}
                              {(editExistingMedia.length + editImages.length) < MAX_IMAGES && (
                                <button onClick={() => editFileInputRef.current?.click()} style={{ width: 72, height: 72, borderRadius: 8, border: '2px dashed var(--border-2)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', cursor: 'pointer', flexShrink: 0 }}>
                                  <ImagePlus size={16} />
                                </button>
                              )}
                            </div>
                          )}
                          {editExistingMedia.length === 0 && editPreviews.length === 0 && (
                            <button onClick={() => editFileInputRef.current?.click()} className="btn btn-soft btn-sm" style={{ marginBottom: 10 }}>
                              <ImagePlus size={13} />Add images
                            </button>
                          )}
                          <input ref={editFileInputRef} type="file" accept="image/*" multiple onChange={handleEditImagePick} style={{ display: 'none' }} />

                          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="meta" style={{ marginTop: 0, color: MAX_CHARS - editCaption.length < 30 ? 'var(--warning)' : 'var(--text-3)' }}>{MAX_CHARS - editCaption.length} chars left</span>
                            <div className="row gap-8">
                              <button onClick={cancelEdit} disabled={savingEdit} className="btn btn-soft btn-sm">Cancel</button>
                              <button onClick={() => saveEdit(post.id)} disabled={savingEdit || !editCaption.trim()} className="btn btn-accent btn-sm">
                                {savingEdit ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={12} />}Save
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p style={{ padding: '0 16px 12px', fontSize: 14, lineHeight: 1.55, color: 'var(--text)', margin: 0 }}>{post.caption}</p>
                      )}

                      {/* Images */}
                      {editingId !== post.id && (post.media ?? []).length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: post.media.length === 1 ? '1fr' : post.media.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '0.5px' }}>
                          {post.media.map((m, i) => (
                            <div key={m.id ?? i} style={{ background: 'var(--surface-2)', overflow: 'hidden', aspectRatio: post.media.length === 1 ? '16/9' : '1' }}>
                              <img src={m.media_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Stats */}
                      {(post.like_count > 0 || post.comment_count > 0) && (
                        <div className="row gap-16" style={{ padding: '10px 16px', borderTop: '1px solid var(--divider)' }}>
                          {post.like_count > 0 && (
                            <span className="meta" style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Heart size={13} style={{ color: 'var(--danger)' }} />{post.like_count} {post.like_count === 1 ? 'reaction' : 'reactions'}
                            </span>
                          )}
                          {post.comment_count > 0 && (
                            <span className="meta" style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                              <MessageCircle size={13} style={{ color: 'var(--info)' }} />{post.comment_count} {post.comment_count === 1 ? 'comment' : 'comments'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {hasMore && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                    <button onClick={loadMore} disabled={loadingMore} className="btn btn-soft" style={{ opacity: loadingMore ? 0.6 : 1 }}>
                      {loadingMore ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />Loading…</> : 'Load more posts'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default ClassFeed;
